import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
} from '@mui/material';
import { SchoolData, DistrictAgg, StatsResult, getSchoolLevel } from './types';
import { SCORECARD_COLORS } from '../../theme/dashboardTheme';

interface ComparisonViewProps {
  filteredSchools: SchoolData[];
  districtAggregates: Map<string, DistrictAgg>;
  stats: StatsResult;
}

type MetricKey = 'total_students' | 'total_schools' | 'ptr' | 'gpi' | 'total_teachers';

interface MetricOption {
  key: MetricKey;
  label: string;
}

const METRIC_OPTIONS: MetricOption[] = [
  { key: 'total_students', label: 'Total Students' },
  { key: 'total_schools', label: 'Total Schools' },
  { key: 'ptr', label: 'PTR' },
  { key: 'gpi', label: 'GPI' },
  { key: 'total_teachers', label: 'Total Teachers' },
];

const getPTRColor = (ptr: number): string => {
  if (ptr <= 40) return SCORECARD_COLORS.excellent;
  if (ptr <= 60) return SCORECARD_COLORS.poor;
  return SCORECARD_COLORS.critical;
};

const getGPIColor = (gpi: number | null): string => {
  if (gpi === null || gpi === undefined) return SCORECARD_COLORS.noData;
  if (gpi >= 0.97 && gpi <= 1.03) return SCORECARD_COLORS.excellent;
  if ((gpi >= 0.8 && gpi < 0.97) || (gpi > 1.03 && gpi <= 1.2)) return SCORECARD_COLORS.poor;
  return SCORECARD_COLORS.critical;
};

const getMetricValue = (district: DistrictAgg, metric: MetricKey): number => {
  switch (metric) {
    case 'total_students':
      return district.total_students;
    case 'total_schools':
      return district.total_schools;
    case 'ptr':
      return district.ptr || 0;
    case 'gpi':
      return district.gpi ?? 0;
    case 'total_teachers':
      return district.total_teachers;
    default:
      return 0;
  }
};

const formatMetricValue = (value: number, metric: MetricKey): string => {
  switch (metric) {
    case 'ptr':
      return value ? `${value.toFixed(1)}:1` : '-';
    case 'gpi':
      return value ? value.toFixed(3) : '-';
    default:
      return value.toLocaleString();
  }
};

const ComparisonView: React.FC<ComparisonViewProps> = ({
  filteredSchools,
  districtAggregates,
  stats,
}) => {
  const [selectedMetric, setSelectedMetric] = useState<MetricKey>('total_students');

  // Convert Map to array and sort by selected metric
  const sortedDistricts = useMemo(() => {
    const districts = Array.from(districtAggregates.values());
    return districts.sort((a, b) => {
      const aVal = getMetricValue(a, selectedMetric);
      const bVal = getMetricValue(b, selectedMetric);
      return bVal - aVal; // descending
    });
  }, [districtAggregates, selectedMetric]);

  // Calculate max value for bar proportions
  const maxValue = useMemo(() => {
    if (sortedDistricts.length === 0) return 1;
    return Math.max(...sortedDistricts.map((d) => getMetricValue(d, selectedMetric)), 1);
  }, [sortedDistricts, selectedMetric]);

  // Calculate averages for the summary row
  const averages = useMemo(() => {
    if (sortedDistricts.length === 0) {
      return { total_students: 0, total_schools: 0, ptr: 0, gpi: 0, total_teachers: 0 };
    }
    const count = sortedDistricts.length;
    const sumStudents = sortedDistricts.reduce((s, d) => s + d.total_students, 0);
    const sumSchools = sortedDistricts.reduce((s, d) => s + d.total_schools, 0);
    const sumTeachers = sortedDistricts.reduce((s, d) => s + d.total_teachers, 0);

    // PTR average: total students / total teachers (weighted, not simple average)
    const avgPTR = sumTeachers > 0 ? sumStudents / sumTeachers : 0;

    // GPI average: weighted by female + male students
    const totalMale = sortedDistricts.reduce((s, d) => s + d.male_students, 0);
    const totalFemale = sortedDistricts.reduce((s, d) => s + d.female_students, 0);
    const avgGPI = totalMale > 0 ? totalFemale / totalMale : null;

    return {
      total_students: Math.round(sumStudents / count),
      total_schools: Math.round(sumSchools / count),
      ptr: avgPTR,
      gpi: avgGPI,
      total_teachers: Math.round(sumTeachers / count),
    };
  }, [sortedDistricts]);

  const getBarColor = (district: DistrictAgg, metric: MetricKey): string => {
    if (metric === 'ptr') return getPTRColor(district.ptr);
    if (metric === 'gpi') return getGPIColor(district.gpi);
    return '#147CD7';
  };

  const getValueColor = (district: DistrictAgg, metric: MetricKey): string | undefined => {
    if (metric === 'ptr') return getPTRColor(district.ptr);
    if (metric === 'gpi') return getGPIColor(district.gpi);
    return undefined;
  };

  return (
    <Box>
      {/* Header with metric selector */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h6" fontWeight={600}>
          District Comparison Ranking
        </Typography>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Sort by</InputLabel>
          <Select
            value={selectedMetric}
            label="Sort by"
            onChange={(e) => setSelectedMetric(e.target.value as MetricKey)}
          >
            {METRIC_OPTIONS.map((opt) => (
              <MenuItem key={opt.key} value={opt.key}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* District ranking table */}
      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{ maxHeight: 'calc(100vh - 340px)' }}
      >
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.50', width: 50 }}>Rank</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.50', minWidth: 140 }}>District</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.50', minWidth: 130 }}>Region</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.50', minWidth: 200 }}>
                {METRIC_OPTIONS.find((m) => m.key === selectedMetric)?.label || 'Value'}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, bgcolor: 'grey.50', minWidth: 90 }}>Students</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, bgcolor: 'grey.50', minWidth: 80 }}>Schools</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, bgcolor: 'grey.50', minWidth: 80 }}>Teachers</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, bgcolor: 'grey.50', minWidth: 70 }}>PTR</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, bgcolor: 'grey.50', minWidth: 70 }}>GPI</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedDistricts.map((district, index) => {
              const metricVal = getMetricValue(district, selectedMetric);
              const barPercent = maxValue > 0 ? (metricVal / maxValue) * 100 : 0;
              const barColor = getBarColor(district, selectedMetric);

              return (
                <TableRow
                  key={district.district_code || district.district_name}
                  hover
                  sx={{ '&:nth-of-type(even)': { bgcolor: 'grey.50' } }}
                >
                  {/* Rank */}
                  <TableCell>
                    <Chip
                      label={index + 1}
                      size="small"
                      sx={{
                        fontWeight: 700,
                        fontSize: 12,
                        height: 24,
                        minWidth: 28,
                        bgcolor: index < 3 ? '#E3F2FD' : 'grey.100',
                        color: index < 3 ? '#0D47A1' : 'text.secondary',
                      }}
                    />
                  </TableCell>

                  {/* District name */}
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {district.district_name}
                    </Typography>
                  </TableCell>

                  {/* Region */}
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {district.region_name}
                    </Typography>
                  </TableCell>

                  {/* Bar indicator + value */}
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box sx={{ flex: 1, minWidth: 80 }}>
                        <LinearProgress
                          variant="determinate"
                          value={barPercent}
                          sx={{
                            height: 10,
                            borderRadius: 5,
                            bgcolor: 'grey.200',
                            '& .MuiLinearProgress-bar': {
                              borderRadius: 5,
                              bgcolor: barColor,
                            },
                          }}
                        />
                      </Box>
                      <Typography
                        variant="body2"
                        fontWeight={700}
                        sx={{
                          minWidth: 60,
                          textAlign: 'right',
                          color: getValueColor(district, selectedMetric),
                        }}
                      >
                        {formatMetricValue(metricVal, selectedMetric)}
                      </Typography>
                    </Box>
                  </TableCell>

                  {/* Students */}
                  <TableCell align="right">
                    <Typography variant="body2">
                      {district.total_students.toLocaleString()}
                    </Typography>
                  </TableCell>

                  {/* Schools */}
                  <TableCell align="right">
                    <Typography variant="body2">
                      {district.total_schools.toLocaleString()}
                    </Typography>
                  </TableCell>

                  {/* Teachers */}
                  <TableCell align="right">
                    <Typography variant="body2">
                      {district.total_teachers.toLocaleString()}
                    </Typography>
                  </TableCell>

                  {/* PTR */}
                  <TableCell align="right">
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{ color: getPTRColor(district.ptr) }}
                    >
                      {district.ptr ? `${district.ptr.toFixed(1)}` : '-'}
                    </Typography>
                  </TableCell>

                  {/* GPI */}
                  <TableCell align="right">
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{ color: getGPIColor(district.gpi) }}
                    >
                      {district.gpi !== null && district.gpi !== undefined
                        ? district.gpi.toFixed(3)
                        : '-'}
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}

            {/* Summary / Average row */}
            {sortedDistricts.length > 0 && (
              <TableRow sx={{ bgcolor: '#E3F2FD', '&:hover': { bgcolor: '#E3F2FD' } }}>
                <TableCell />
                <TableCell>
                  <Typography variant="body2" fontWeight={700} color="primary.dark">
                    Average (all districts)
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary" fontStyle="italic">
                    {sortedDistricts.length} districts
                  </Typography>
                </TableCell>
                <TableCell />
                <TableCell align="right">
                  <Typography variant="body2" fontWeight={700}>
                    {averages.total_students.toLocaleString()}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight={700}>
                    {averages.total_schools.toLocaleString()}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight={700}>
                    {averages.total_teachers.toLocaleString()}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography
                    variant="body2"
                    fontWeight={700}
                    sx={{ color: getPTRColor(averages.ptr) }}
                  >
                    {averages.ptr ? averages.ptr.toFixed(1) : '-'}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography
                    variant="body2"
                    fontWeight={700}
                    sx={{ color: getGPIColor(averages.gpi) }}
                  >
                    {averages.gpi !== null ? averages.gpi.toFixed(3) : '-'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {sortedDistricts.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                  <Typography variant="body1" color="text.secondary">
                    No district data available for comparison.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ComparisonView;
